import { axiosInstance } from './api'

const API_BASE_URL = process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING?.replace(/\/$/, '') + '/api'

// Default headers for all requests
const defaultHeaders = {
  'Content-Type': 'application/json',
}


// Helper function to make API calls
const makeRequest = async (method, url, data = null, params = null) => {
  try {
    const config = {
      method,
      url: `${API_BASE_URL}${url}`,
      headers: defaultHeaders,
      timeout: 30000,
    }

    if (data) {
      config.data = data
    }

    if (params) {
      config.params = params
    }

    const response = await axiosInstance(config)
    return response.data
  } catch (error) {
    console.error(`Error making ${method} request to ${url}:`, error)
    throw new Error(
      error.response?.data?.message || `Failed to ${method.toLowerCase()} data`
    )
  }
}

// Get all notifications with filters
export const getNotifications = async (params = {}) => {
  const queryParams = {}

  // Add all parameters to query object
  Object.keys(params).forEach((key) => {
    if (
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ''
    ) {
      queryParams[key] = params[key]
    }
  })

  return await makeRequest('GET', '/notifications', null, queryParams)
}

// Get single notification by ID
export const getNotificationById = async (id) => {
  return await makeRequest('GET', `/notifications/${id}`)
}

// Create new notification
export const createNotification = async (notificationData) => {
  return await makeRequest('POST', '/notifications', notificationData)
}

// Update notification status
export const updateNotificationStatus = async (id, updates) => {
  return await makeRequest('PUT', `/notifications/${id}`, updates)
}

// Get notification statistics
export const getNotificationStats = async (days = 30) => {
  return await makeRequest('GET', '/notifications/stats', null, { days })
}

// Delete notification
export const deleteNotification = async (id) => {
  return await makeRequest('DELETE', `/notifications/${id}`)
}

// Bulk update notifications
export const bulkUpdateNotifications = async (ids, updates) => {
  return await makeRequest('PUT', '/notifications/bulk-update', {
    ids,
    updates,
  })
}

// Mark notification as read
export const markAsRead = async (id) => {
  return await updateNotificationStatus(id, {
    status: 'resolved',
    processed: true,
  })
}

// Mark multiple notifications as read
export const markMultipleAsRead = async (ids) => {
  return await bulkUpdateNotifications(ids, {
    status: 'resolved',
    processed: true,
  })
}

// Export notifications to CSV
export const exportNotifications = async (params = {}) => {
  try {
    // Get all notifications with the current filters (no pagination)
    const exportParams = { ...params, limit: 10000, page: 1 }
    const response = await getNotifications(exportParams)
    return response.data?.notifications || response.notifications || response
  } catch (error) {
    console.error('Error exporting notifications:', error)
    throw new Error('Failed to export notifications')
  }
}

// Bulk create multiple notifications
export const createMultipleNotifications = async (notificationsArray) => {
  return await makeRequest('POST', '/notifications/bulk-create', {
    notifications: notificationsArray,
  })
}

// Update notification priority
export const updateNotificationPriority = async (id, priority) => {
  return await updateNotificationStatus(id, { priority })
}

// Get notifications by type
export const getNotificationsByType = async (type, params = {}) => {
  return await getNotifications({ ...params, type })
}

// Get notifications by status
export const getNotificationsByStatus = async (status, params = {}) => {
  return await getNotifications({ ...params, status })
}

// Helper function to build query parameters for API calls
export const buildNotificationParams = (filters) => {
  const params = {}

  if (filters.searchName) params.search = filters.searchName
  if (filters.selectedStatus) params.status = filters.selectedStatus
  if (filters.selectedSeverity) params.severity = filters.selectedSeverity
  if (filters.selectedType) params.type = filters.selectedType
  if (filters.fromDate) params.fromDate = filters.fromDate.toISOString()
  if (filters.toDate) params.toDate = filters.toDate.toISOString()
  if (filters.plantId) params.plantId = filters.plantId
  if (filters.page) params.page = filters.page
  if (filters.limit) params.limit = filters.limit
  if (filters.sortBy) params.sortBy = filters.sortBy
  if (filters.sortOrder) params.sortOrder = filters.sortOrder

  return params
}

// Default export with all functions
const notificationService = {
  getNotifications,
  getNotificationById,
  createNotification,
  updateNotificationStatus,
  getNotificationStats,
  deleteNotification,
  bulkUpdateNotifications,
  markAsRead,
  markMultipleAsRead,
  exportNotifications,
  createMultipleNotifications,
  updateNotificationPriority,
  getNotificationsByType,
  getNotificationsByStatus,
  buildNotificationParams,
}

export default notificationService
